import apiClient from '../../config/axiosConfig';
import { API_BASE_URL } from '../../utils/apiUtils';

// ============================================================================
// CONSTANTS
// ============================================================================

export const TEAM_ASSET_TYPES = {
    LOGO: 'logo',
    COVER_PHOTO: 'cover_photo',
    VIRTUAL_BACKGROUND: 'virtual_background',
    LINK_ICON: 'link_icon'
};

const VALID_ASSET_TYPES = Object.values(TEAM_ASSET_TYPES);

// ============================================================================
// VALIDATION FUNCTIONS
// ============================================================================

const validateTeamId = (teamId) => {
    if (!teamId || typeof teamId !== 'string' || teamId.trim() === '') {
        throw new Error('Team ID is required and must be a valid string');
    }
    return teamId.trim();
};

const validateAssetId = (assetId) => {
    if (!assetId || typeof assetId !== 'string' || assetId.trim() === '') {
        throw new Error('Asset ID is required and must be a valid string');
    }
    return assetId.trim();
};

const validateAssetType = (type) => {
    if (!type || typeof type !== 'string' || !VALID_ASSET_TYPES.includes(type)) {
        throw new Error('Asset type is invalid');
    }
    return type;
};

const validateAssetUrl = (url) => {
    if (!url || typeof url !== 'string' || url.trim() === '') {
        throw new Error('Asset URL is required');
    }
    if (url.startsWith('blob:')) {
        throw new Error('Asset must be uploaded before saving');
    }
    return url.trim();
};

// ============================================================================
// ERROR HANDLER
// ============================================================================

const handleTeamAssetApiError = (error, operation = 'Team Asset API operation') => {
    console.error(`[TeamAssetAPI] ${operation} Error:`, {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
        requestUrl: error.config?.url,
        requestMethod: error.config?.method
    });

    if (error.response?.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('authUser');
        throw new Error('Your session has expired. Please log in again');
    }

    if (error.response?.status === 403) {
        throw new Error('You do not have permission to perform this action');
    }

    if (error.response?.status === 404) {
        throw new Error('Team asset not found');
    }

    if (error.response?.status >= 500) {
        throw new Error('Server error. Please try again later.');
    }

    if (error.code === 'ERR_NETWORK') {
        throw new Error('Network error. Please check your connection and try again.');
    }

    if (error.code === 'ECONNABORTED') {
        throw new Error('Request timeout. Please try again.');
    }

    const errorMessage = error.response?.data?.message || error.message || 'An unexpected error occurred';
    throw new Error(errorMessage);
};

// ============================================================================
// TEAM ASSET CRUD OPERATIONS
// ============================================================================

/**
 * Create a new team asset
 * POST /api/team/:teamId/assets
 */
export const createTeamAsset = async (teamId, assetData) => {
    try {
        const validTeamId = validateTeamId(teamId);
        const validType = validateAssetType(assetData.type);
        const validUrl = validateAssetUrl(assetData.url);

        const payload = {
            type: validType,
            url: validUrl,
            name: assetData.name || '',
            linkType: assetData.linkType || null,
            metadata: assetData.metadata || {}
        };

        const response = await apiClient.post(
            `${API_BASE_URL}/api/team/${validTeamId}/assets`,
            payload
        );

        return response.data;
    } catch (error) {
        handleTeamAssetApiError(error, 'Creating team asset');
    }
};

/**
 * Get team assets (optionally filter by type)
 * GET /api/team/:teamId/assets
 */
export const getTeamAssets = async (teamId, type = null) => {
    try {
        const validTeamId = validateTeamId(teamId);
        const params = {};

        if (type) {
            params.type = validateAssetType(type);
        }

        const response = await apiClient.get(
            `${API_BASE_URL}/api/team/${validTeamId}/assets`,
            { params }
        );

        return response.data;
    } catch (error) {
        handleTeamAssetApiError(error, 'Fetching team assets');
    }
};

/**
 * Get a single team asset
 * GET /api/team/:teamId/assets/:assetId
 */
export const getTeamAsset = async (teamId, assetId) => {
    try {
        const validTeamId = validateTeamId(teamId);
        const validAssetId = validateAssetId(assetId);

        const response = await apiClient.get(
            `${API_BASE_URL}/api/team/${validTeamId}/assets/${validAssetId}`
        );

        return response.data;
    } catch (error) {
        handleTeamAssetApiError(error, 'Fetching team asset');
    }
};

/**
 * Update a team asset
 * PUT /api/team/:teamId/assets/:assetId
 */
export const updateTeamAsset = async (teamId, assetId, updateData) => {
    try {
        const validTeamId = validateTeamId(teamId);
        const validAssetId = validateAssetId(assetId);

        const payload = {};
        if (updateData.url !== undefined) {
            payload.url = validateAssetUrl(updateData.url);
        }
        if (updateData.name !== undefined) {
            payload.name = updateData.name || '';
        }
        if (updateData.linkType !== undefined) {
            payload.linkType = updateData.linkType || null;
        }
        if (updateData.metadata !== undefined) {
            payload.metadata = updateData.metadata || {};
        }

        const response = await apiClient.put(
            `${API_BASE_URL}/api/team/${validTeamId}/assets/${validAssetId}`,
            payload
        );

        return response.data;
    } catch (error) {
        handleTeamAssetApiError(error, 'Updating team asset');
    }
};

/**
 * Delete a team asset
 * DELETE /api/team/:teamId/assets/:assetId
 */
export const deleteTeamAsset = async (teamId, assetId) => {
    try {
        const validTeamId = validateTeamId(teamId);
        const validAssetId = validateAssetId(assetId);

        const response = await apiClient.delete(
            `${API_BASE_URL}/api/team/${validTeamId}/assets/${validAssetId}`
        );

        return response.data;
    } catch (error) {
        handleTeamAssetApiError(error, 'Deleting team asset');
    }
};
